/**
 * The ONE root for every language-shaped site in the app — the set of
 * languages Tessa speaks, and the tables that hang off it.
 *
 * Zero imports and client-safe on purpose: client components, the pure
 * i18n modules (lib/routines/member-messages.ts, lib/routines/card.ts,
 * lib/channels/link-code-copy.ts …), the API routes and the Mastra schemas
 * all derive from here, so widening the set is a one-line change to
 * SUPPORTED_LANGS and the compiler then enumerates every Record keyed by
 * SupportedLang that is missing the new column.
 *
 * Rules this module keeps:
 *   • A value is a SupportedLang only when it is LITERALLY one of the codes —
 *     "es-MX" is not "es", "" is nothing. Callers that want lenient parsing
 *     go through resolveLang (trim + lowercase, then the same exact match).
 *   • The Intl maps are per-surface on purpose: the routine card renders
 *     English dates in en-CA and the consent page / provisional-link dates
 *     render Spanish in es-ES ("1 sept") where the member messages use es-MX
 *     ("1 sep"). Each map pins the locale its fixtures were written against —
 *     do not fold them together, that changes rendered bytes.
 *   • Portuguese (2026-09-08) is stored as the bare "pt" (users.preferred_language
 *     is CHAR(2)) and rendered as BRAZILIAN Portuguese everywhere — pt-BR
 *     locale, "você" register, 24-hour "9h / 16h30" clock. pt-PT is a
 *     non-goal; +351 Portugal simply maps to the same column.
 */

export const SUPPORTED_LANGS = ['en', 'es', 'fr', 'pt'] as const;

export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

/** Exact match only: 'es' yes, 'ES' / 'es-MX' / '' / null no. */
export function isSupportedLang(v: unknown): v is SupportedLang {
  return typeof v === 'string' && (SUPPORTED_LANGS as readonly string[]).includes(v);
}

/**
 * Lenient resolver — the historical resolveMemberLang contract: a string is
 * trimmed and lowercased, then must equal a code exactly; anything else
 * (non-strings, regional tags like 'es-MX', unknown codes) is the fallback.
 */
export function resolveLang(raw: unknown, fallback: SupportedLang = 'en'): SupportedLang {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return isSupportedLang(v) ? v : fallback;
}

/**
 * Where a reader's language came from: their own profile
 * (users.preferred_language), the workspace language
 * (domains.default_transcript_language, the "Workspace language" setting), or
 * neither (English).
 */
export type ReaderLangSource = 'profile' | 'workspace' | 'fallback';

/**
 * The workspace language a reader can inherit, from the RAW
 * domains.default_transcript_language value. That column is the transcription
 * language (AssemblyAI codes plus 'auto', DEFAULT 'en'), so only a value that
 * trims and lowercases to a supported code is a reader language: 'auto', NULL,
 * '' and every other transcription code ('de', 'it', …) are no workspace
 * language (null).
 */
export function workspaceReaderLang(raw: unknown): SupportedLang | null {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
  return isSupportedLang(v) ? v : null;
}

/**
 * The reader-language order for written deliverables:
 * users.preferred_language → the workspace language → English.
 *   • The profile is lenient (resolveLang's trim + lowercase). A profile
 *     language that is set but NOT supported ('de', 'es-MX') falls through to
 *     the workspace language — the reader cannot be served in theirs, and the
 *     workspace's is the better guess than English.
 *   • The workspace input is the raw column (workspaceReaderLang). Workspace
 *     'en' resolves with source 'workspace' — it is the column DEFAULT for
 *     most workspaces, so it proves no choice; labels render it as plain
 *     "English" (lib/routines/recipient-candidate-view.ts).
 */
export function resolveReaderLang(profileRaw: unknown, workspaceRaw: unknown): { lang: SupportedLang; source: ReaderLangSource } {
  const p = typeof profileRaw === 'string' ? profileRaw.trim().toLowerCase() : '';
  if (isSupportedLang(p)) return { lang: p, source: 'profile' };
  const w = workspaceReaderLang(workspaceRaw);
  if (w) return { lang: w, source: 'workspace' };
  return { lang: 'en', source: 'fallback' };
}

/** Default Intl locale per language (member messages: en-US / es-MX / fr-FR / pt-BR). */
export const INTL_LOCALE: Record<SupportedLang, string> = {
  en: 'en-US',
  es: 'es-MX',
  fr: 'fr-FR',
  pt: 'pt-BR',
};

/** The routine card's next-run stamp renders English in en-CA (fixtures pin "Mon, Sep 1, 4:00 p.m."). */
export const INTL_LOCALE_CA: Record<SupportedLang, string> = {
  en: 'en-CA',
  es: 'es-MX',
  fr: 'fr-FR',
  pt: 'pt-BR',
};

/** Long-form dates on the consent page and the provisional-link expiry: Spanish in es-ES ("1 sept", not es-MX's "1 sep"). */
export const INTL_LOCALE_DATE: Record<SupportedLang, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  pt: 'pt-BR',
};

/** cronstrue/i18n locale keys — the card's schedule sentence. cronstrue ships pt_BR, not pt. */
export const CRONSTRUE_LOCALE: Record<SupportedLang, string> = {
  en: 'en',
  es: 'es',
  fr: 'fr',
  pt: 'pt_BR',
};

/**
 * Choose one column of a per-language table. `null`/`undefined` (and any
 * value that is not literally a code) fall back to English — the same truth
 * table as the historical es/fr ternary chains this replaces, except the table is a Record<SupportedLang, T>, so
 * widening SUPPORTED_LANGS fails to compile at every call site that has not
 * been given the new column.
 */
export function pickLang<T>(lang: SupportedLang | null | undefined, table: Record<SupportedLang, T>): T {
  return table[isSupportedLang(lang) ? lang : 'en'];
}

/** Whether a language renders times on a 24-hour clock ("16:00", never "4:00 p.m.").
 *  A Record, not a Set, so a new language must DECIDE (the compiler asks).
 *  pt-BR writes "9h" / "16h30" — a 24-hour clock in its own shape. */
export const TWENTY_FOUR_HOUR_CLOCK: Record<SupportedLang, boolean> = {
  en: false,
  es: false,
  fr: true,
  pt: true,
};

/** Total lookup — anything outside the set reads as English (12-hour). */
export function uses24HourClock(lang: SupportedLang | null | undefined): boolean {
  return pickLang(lang, TWENTY_FOUR_HOUR_CLOCK);
}

/** English names — admin surfaces and logs. */
export const LANG_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  pt: 'Portuguese',
};

/** Each language's own name for itself — what a person picks in Settings (UI, never SMS — accents are fine). */
export const NATIVE_LABELS: Record<SupportedLang, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
};
